import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Bell,
    FileText,
    LayoutGrid,
    Loader2,
    MessageSquare,
    RefreshCw,
    Save,
    Settings2,
    Shield,
    Smartphone,
    ToggleLeft,
    Users,
} from "lucide-react";
import { toast } from "sonner";
import { muvekkilPanelAyar } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const SEKME_GRUPLARI = [
    {
        baslik: "Genel sekmeler",
        ikon: LayoutGrid,
        alanlar: [
            { key: "modul_ozet", label: "Özet" },
            { key: "modul_bildirim", label: "Bildirimler" },
            { key: "modul_dosya", label: "Dosya detayı" },
            { key: "modul_evrak", label: "Evraklarım" },
            { key: "modul_mesaj", label: "Mesajlar" },
            { key: "modul_odeme", label: "Ödemeler" },
            { key: "modul_deger", label: "Değer kaybı" },
            { key: "modul_icra", label: "İcra" },
            { key: "modul_tahkim", label: "Tahkim" },
            { key: "modul_akil", label: "Akıl asistanı" },
            { key: "modul_profil", label: "Profil" },
        ],
    },
    {
        baslik: "Özet kartları",
        ikon: FileText,
        alanlar: [
            { key: "modul_onboarding", label: "Onboarding" },
            { key: "modul_on_tahkim", label: "Ön tahkim süresi" },
            { key: "modul_eksper", label: "Eksper rehberi" },
            { key: "modul_evrak_checklist", label: "Evrak checklist" },
            { key: "modul_finans_ozet", label: "Finansal özet" },
            { key: "modul_admin_kopru", label: "Bürodan güncellemeler" },
        ],
    },
];

const PUSH_TURLERI = [
    { key: "push_bildirim_aktif", label: "Push (genel anahtar)", aciklama: "Kapalıysa hiçbir push gitmez." },
    { key: "push_dosya_durum_aktif", label: "Dosya durumu değişince", aciklama: "Sigorta dosyası aşama/durum güncellemesi." },
    { key: "push_evrak_aktif", label: "Evrak değişince", aciklama: "Yükleme, onay, red vb." },
    { key: "push_odeme_aktif", label: "Ödeme değişince", aciklama: "Yeni ödeme veya ödeme durumu." },
    { key: "push_mesaj_aktif", label: "Mesaj gelince", aciklama: "Büro ↔ müvekkil mesajları." },
    { key: "push_tahkim_aktif", label: "Tahkim güncellemesi", aciklama: "Tahkim süreci değişiklikleri." },
    { key: "push_icra_aktif", label: "İcra güncellemesi", aciklama: "İcra dosyası hareketleri." },
    { key: "push_deger_kaybi_aktif", label: "Değer kaybı", aciklama: "Değer kaybı modülü bildirimleri." },
    { key: "push_sistem_aktif", label: "Sistem / e-posta", aciklama: "E-posta eşleme ve sistem olayları." },
];

const OZELLIKLER = [
    { key: "panel_aktif", label: "Panel erişimi", ikon: Users, aciklama: "Kapalıyken müvekkiller giriş yapamaz." },
    { key: "mesajlasma_aktif", label: "Dosya mesajlaşması", ikon: MessageSquare, aciklama: "Müvekkil ↔ büro mesajları." },
    { key: "sifremi_unuttum_aktif", label: "Şifremi unuttum", ikon: Shield, aciklama: "Giriş ekranındaki sıfırlama linki." },
    { key: "evrak_yukleme_aktif", label: "Evrak yükleme", ikon: FileText },
    { key: "evrak_silme_aktif", label: "Evrak silme", ikon: FileText },
    { key: "coklu_dosya_aktif", label: "Çoklu dosya seçici", ikon: LayoutGrid },
];

function ToggleRow({ label, checked, onChange, aciklama, icon }) {
    return (
        <label className="flex items-start justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 cursor-pointer hover:bg-white/[0.05] transition-colors">
            <div>
                <div className="text-sm font-medium text-slate-100 flex items-center gap-2">{icon}{label}</div>
                {aciklama && <p className="text-xs text-slate-500 mt-0.5">{aciklama}</p>}
            </div>
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                onClick={() => onChange(!checked)}
                className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${
                    checked ? "bg-emerald-500/80" : "bg-slate-600"
                }`}
            >
                <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${
                        checked ? "translate-x-5" : "translate-x-0.5"
                    }`}
                />
            </button>
        </label>
    );
}

export default function MuvekkilPanelAyarPage() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [state, setState] = useState({});
    const [bakimMesaji, setBakimMesaji] = useState("");
    const [lastApplied, setLastApplied] = useState(null);
    const pendingPatch = useRef({});
    const saveTimer = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await muvekkilPanelAyar.get();
            const flat = {};
            for (const [k, v] of Object.entries(data.moduller || {})) {
                flat[`modul_${k}`] = v;
            }
            for (const [k, v] of Object.entries(data.ozellikler || {})) {
                flat[k] = v;
            }
            for (const [k, v] of Object.entries(data.push_ayarlari || {})) {
                flat[k] = v;
            }
            setState(flat);
            setBakimMesaji(data.bakim_mesaji || "");
            setLastApplied(data.guncelleme_tarihi || null);
            pendingPatch.current = {};
        } catch {
            toast.error("Panel ayarları yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        return () => {
            if (saveTimer.current) clearTimeout(saveTimer.current);
        };
    }, []);

    const flushSave = useCallback(async () => {
        const payload = { ...pendingPatch.current };
        if (!Object.keys(payload).length) return;
        pendingPatch.current = {};
        setSaving(true);
        try {
            const res = await muvekkilPanelAyar.patch(payload);
            setLastApplied(res?.guncelleme_tarihi || new Date().toISOString());
            toast.success("Müvekkil paneline anında uygulandı", { duration: 2200 });
        } catch (e) {
            Object.assign(pendingPatch.current, payload);
            toast.error(e?.response?.data?.detail || "Kayıt başarısız.");
        } finally {
            setSaving(false);
        }
    }, []);

    function scheduleSave(key, val) {
        pendingPatch.current[key] = val;
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => {
            flushSave();
        }, 400);
    }

    function setField(key, val) {
        setState((s) => ({ ...s, [key]: val }));
        scheduleSave(key, val);
    }

    async function save() {
        if (saveTimer.current) {
            clearTimeout(saveTimer.current);
            saveTimer.current = null;
        }
        await flushSave();
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[40vh] text-slate-400 gap-2">
                <Loader2 className="h-5 w-5 animate-spin" />
                Yükleniyor…
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-6 pb-12">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <p className="text-xs uppercase tracking-widest text-[#D4AF37]/80 mb-1">Operasyon</p>
                    <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
                        <Settings2 className="h-6 w-6 text-[#D4AF37]" />
                        Müvekkil Panel Ayarları
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">
                        mbshukuk.com/panel — her değişiklik otomatik kaydedilir; açık müvekkil
                        oturumları birkaç saniye içinde güncellenir.
                    </p>
                    {lastApplied && (
                        <p className="text-[11px] text-emerald-400/80 mt-1">
                            Son uygulama: {new Date(lastApplied).toLocaleString("tr-TR")}
                        </p>
                    )}
                </div>
                <div className="flex gap-2 items-center">
                    {saving && (
                        <span className="text-xs text-amber-300/90 inline-flex items-center gap-1">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Uygulanıyor…
                        </span>
                    )}
                    <Button variant="outline" size="sm" onClick={load} disabled={saving}>
                        <RefreshCw className="h-4 w-4 mr-1" />
                        Yenile
                    </Button>
                    <Button size="sm" onClick={save} disabled={saving} variant="outline">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                        Şimdi uygula
                    </Button>
                </div>
            </div>

            <section className="rounded-2xl border border-white/10 bg-slate-900/50 p-5 space-y-3">
                <h2 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
                    <Smartphone className="h-4 w-4 text-emerald-400" />
                    Push bildirim türleri
                </h2>
                <p className="text-xs text-slate-500">
                    İlgili panel modülü kapalıysa o modülün push&apos;u da gitmez. Örn. Evrak sekmesi kapalı → evrak push yok.
                </p>
                <div className="space-y-2">
                    {PUSH_TURLERI.map(({ key, label, aciklama }) => (
                        <ToggleRow
                            key={key}
                            label={label}
                            icon={<Bell className="h-4 w-4 text-slate-500" />}
                            aciklama={aciklama}
                            checked={!!state[key]}
                            onChange={(v) => setField(key, v)}
                        />
                    ))}
                </div>
            </section>

            <section className="rounded-2xl border border-white/10 bg-slate-900/50 p-5 space-y-3">
                <h2 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
                    <Bell className="h-4 w-4 text-amber-400" />
                    Özellikler
                </h2>
                <div className="space-y-2">
                    {OZELLIKLER.map(({ key, label, ikon: Icon, aciklama }) => (
                        <ToggleRow
                            key={key}
                            label={label}
                            icon={<Icon className="h-4 w-4 text-slate-500" />}
                            aciklama={aciklama}
                            checked={!!state[key]}
                            onChange={(v) => setField(key, v)}
                        />
                    ))}
                </div>
                <div className="pt-2">
                    <label className="text-xs text-slate-500 block mb-1">Bakım mesajı (panel kapalıyken)</label>
                    <Textarea
                        value={bakimMesaji}
                        onChange={(e) => {
                            setBakimMesaji(e.target.value);
                            scheduleSave("bakim_mesaji", e.target.value);
                        }}
                        rows={2}
                        placeholder="Örn: Panel bakımda. Lütfen daha sonra tekrar deneyin."
                        className="bg-black/20 border-white/10"
                    />
                </div>
            </section>

            {SEKME_GRUPLARI.map((grup) => (
                <section
                    key={grup.baslik}
                    className="rounded-2xl border border-white/10 bg-slate-900/50 p-5 space-y-3"
                >
                    <h2 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
                        <grup.ikon className="h-4 w-4 text-[#D4AF37]" />
                        {grup.baslik}
                    </h2>
                    <div className="grid sm:grid-cols-2 gap-2">
                        {grup.alanlar.map(({ key, label }) => (
                            <ToggleRow
                                key={key}
                                label={label}
                                checked={!!state[key]}
                                onChange={(v) => setField(key, v)}
                            />
                        ))}
                    </div>
                </section>
            ))}

            <p className="text-xs text-emerald-500/70 flex items-center gap-1">
                <ToggleLeft className="h-3 w-3" />
                Anahtarları çevirdiğinizde ayar anında kaydedilir; müvekkil paneli ~5 sn içinde yenilenir.
            </p>
        </div>
    );
}
